import React, { ReactNode, CSSProperties } from 'react';
import classNames from 'classnames';
import isobject from 'lodash.isobject';

const colWidths = ['col', 'xs', 'sm', 'md', 'lg', 'xl'];

type Types = {
  size: boolean | number | string;
  order: number | string;
  offset: number | string;
};

type ColumnProps = boolean | number | string | Types;

type Props = {
  tag?: any;
  id?: number | string;
  xs?: ColumnProps;
  sm?: ColumnProps;
  md?: ColumnProps;
  lg?: ColumnProps;
  xl?: ColumnProps;
  col?: ColumnProps;
  className?: string;
  cssModule?: any;
  widths?: string[];
  children?: ReactNode;
  style?: CSSProperties;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
} & Partial<DefaultProps>;

type DefaultProps = {
  tag: 'div';
  widths: string[];
};

const defaultProps: DefaultProps = {
  tag: 'div',
  widths: colWidths,
};

const getColumnSizeClass = (
  isXs: boolean,
  colWidth: string,
  colSize: ColumnProps,
) => {
  if (colSize === true || colSize === '') {
    return isXs ? 'col' : `col-${colWidth}`;
  }
  if (colSize === 'auto') {
    return isXs ? 'col-auto' : `col-${colWidth}-auto`;
  }

  if (isXs) {
    return `col-${colSize}`;
  }

  return colWidth !== 'col' ? `col-${colWidth}-${colSize}` : `col-${colSize}`;
};

const Col = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { className, cssModule, widths, tag: Tag, ...attributes } = props;

  const colClasses = [];

  widths.forEach(colWidth => {
    const columnProp: ColumnProps = props[colWidth];

    delete attributes[colWidth];

    if (!columnProp && columnProp !== '') {
      return;
    }

    const isXs = colWidth === 'xs';

    if (isobject(columnProp)) {
      const colSizeInterfix = isXs ? '-' : `-${colWidth}-`;
      const colClass = getColumnSizeClass(isXs, colWidth, columnProp.size);

      colClasses.push(
        classNames({
          [colClass]: columnProp.size || columnProp.size === '',
          [`order${colSizeInterfix}${columnProp.order}`]:
            columnProp.order || columnProp.order === 0,
          [`offset${colSizeInterfix}${columnProp.offset}`]:
            columnProp.offset || columnProp.offset === 0,
        }),
        cssModule,
      );
    } else {
      const colClass = getColumnSizeClass(isXs, colWidth, columnProp);
      colClasses.push(colClass);
    }
  });

  if (!colClasses.length) {
    colClasses.push('col');
  }

  const classes = classNames(className, colClasses);

  return <Tag {...attributes} ref={ref} className={classes} />;
});

Col.defaultProps = defaultProps;

export default Col;
